/**
 * Priority Indicator Component
 * Displays order priority with visual indicator.
 * Only shows for rush and urgent priorities.
 * 
 * Extracted from App.js for better code organization.
 */

import React from 'react';

/**
 * Get priority configuration based on priority value
 */
const getPriorityConfig = (priority) => {
  switch (priority?.toLowerCase()) {
    case 'urgent':
      return { 
        show: true,
        className: 'bg-red-500 text-white',
        label: '!',
        title: 'Urgent Priority'
      };
    case 'rush':
      return { 
        show: true,
        className: 'bg-blue-500 text-white',
        label: 'R',
        title: 'Rush Priority'
      };
    case 'normal':
    default:
      return { 
        show: false,
        className: '',
        label: '',
        title: 'Normal Priority'
      };
  }
};

/**
 * Priority dot component for compact display
 */
export const PriorityDot = ({ priority }) => {
  const config = getPriorityConfig(priority);
  
  if (!config.show) return null;
  
  return (
    <span 
      className={`w-2 h-2 rounded-full ${priority === 'urgent' ? 'bg-red-500' : 'bg-blue-500'}`}
      title={config.title}
    />
  );
};

/**
 * PriorityIndicator Component
 * 
 * @param {string} priority - Priority level ('normal', 'rush', 'urgent')
 */
export const PriorityIndicator = ({ priority }) => {
  const config = getPriorityConfig(priority);
  
  // Only show indicator for urgent and rush (normal is not displayed)
  if (!config.show) {
    return null;
  }
  
  return (
    <div 
      className={`${config.className} w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shadow-sm`}
      title={config.title}
      data-testid={`priority-indicator-${priority?.toLowerCase()}`}
    >
      {config.label}
    </div>
  );
};

export { getPriorityConfig };
export default PriorityIndicator;
